// src/components/HouseholdSettings.js
import { useState, useEffect } from "react";
import StoreSettings from "./StoreSettings";
import { useAuth } from "../AuthContext";
import { useHousehold } from "../HouseholdContext";
import { createInvite, getPendingInvites, deleteInvite, updateHouseholdName, removeMember, leaveHousehold } from "../householdService";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function HouseholdSettings({ onHouseholdLeft }) {
  const { user } = useAuth();
  const { household, setHousehold } = useHousehold();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [pendingInvites, setPendingInvites] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(household?.name || "");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [promotingId, setPromotingId] = useState(null);

  const isOwner = household?.ownerId === user?.uid;

  useEffect(() => {
    if (household) { loadPendingInvites(); setNewName(household.name); }
  }, [household?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPendingInvites() {
    const invites = await getPendingInvites(household.id);
    setPendingInvites(invites);
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setLoading(true); setError(""); setInviteLink("");
    try {
      const token = await createInvite(household.id, household.name, user.displayName || user.email.split("@")[0], inviteEmail.trim());
      const link = `${window.location.origin}?invite=${token}`;
      setInviteLink(link);
      setInviteEmail("");
      setSuccess("Invite sent! They will see a notification next time they open Homie.");
      loadPendingInvites();
    } catch (err) { setError("Failed to create invite. Please try again."); }
    finally { setLoading(false); }
  }

  async function handleCopyLink() {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  }

  async function handleCancelInvite(inviteId) {
    await deleteInvite(inviteId); loadPendingInvites();
  }

  async function handleSaveName(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    await updateHouseholdName(household.id, newName.trim());
    setHousehold(h => ({ ...h, name: newName.trim() }));
    setEditingName(false); setSuccess("Household name updated!");
  }

  async function handleRemoveMember(member) {
    if (!window.confirm(`Remove ${member.displayName} from the household?`)) return;
    await removeMember(household.id, member.uid, member);
    setHousehold(h => ({ ...h, memberIds: h.memberIds.filter(id => id !== member.uid), members: h.members.filter(m => m.uid !== member.uid) }));
    setSuccess(`${member.displayName} has been removed.`);
  }

  async function handlePromoteToOwner(member) {
    if (!window.confirm(`Promote ${member.displayName} to Owner? You will become a regular member.`)) return;
    setPromotingId(member.uid);
    try {
      const updatedMembers = household.members.map(m => {
        if (m.uid === member.uid) return { ...m, role: "owner" };
        if (m.uid === user.uid) return { ...m, role: "member" };
        return m;
      });
      await updateDoc(doc(db, "households", household.id), { ownerId: member.uid, members: updatedMembers });
      setHousehold(h => ({ ...h, ownerId: member.uid, members: updatedMembers }));
      setSuccess(`${member.displayName} is now the Owner!`);
    } catch (err) { setError("Failed to promote member. Please try again."); }
    finally { setPromotingId(null); }
  }

  async function handleLeave() {
    if (!window.confirm("Are you sure you want to leave this household?")) return;
    await leaveHousehold(household.id, user); onHouseholdLeft();
  }

  return (
    <div className="fade-in" style={{ maxWidth: 680 }}>
      {success && <div style={styles.successBanner} onClick={() => setSuccess("")}>{success} <span style={{ float: "right", cursor: "pointer" }}>×</span></div>}
      {error && <div style={styles.errorBanner} onClick={() => setError("")}>{error} <span style={{ float: "right", cursor: "pointer" }}>×</span></div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h3 style={styles.sectionTitle}>Household Name</h3>
          {isOwner && !editingName && <button className="btn-secondary" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setEditingName(true)}>Edit</button>}
        </div>
        {editingName ? (
          <form onSubmit={handleSaveName} style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <input className="field-input" value={newName} onChange={e => setNewName(e.target.value)} style={{ flex: 1 }} />
            <button className="btn-primary" type="submit" style={{ padding: "10px 18px" }}>Save</button>
            <button className="btn-secondary" type="button" style={{ padding: "10px 18px" }} onClick={() => setEditingName(false)}>Cancel</button>
          </form>
        ) : (
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#8B4513", marginTop: 6 }}>{household?.name}</div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={styles.sectionTitle}>Household Members</h3>
        <div style={{ marginTop: 12 }}>
          {household?.members?.map(member => (
            <div key={member.uid} style={styles.memberRow}>
              <div style={styles.avatar}>
                {member.photoURL
                  ? <img src={member.photoURL} alt="" style={{ width: 40, height: 40, borderRadius: "50%" }} referrerPolicy="no-referrer" />
                  : <div style={styles.avatarFallback}>{(member.displayName || "?")[0].toUpperCase()}</div>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#2c1810" }}>
                  {member.displayName}
                  {member.uid === user.uid && <span style={styles.youBadge}>You</span>}
                </div>
                <div style={{ fontSize: 12, color: "#a07850", fontWeight: 600 }}>{member.email}</div>
              </div>
              <span style={{ ...styles.roleBadge, ...(member.role === "owner" ? styles.ownerBadge : styles.memberBadge) }}>
                {member.role === "owner" ? "👑 Owner" : "Member"}
              </span>
              {isOwner && member.uid !== user.uid && (
                <div style={{ display: "flex", gap: 6 }}>
                  {member.role !== "owner" && (
                    <button onClick={() => handlePromoteToOwner(member)} disabled={promotingId === member.uid} style={styles.promoteBtn} title="Promote to Owner">
                      {promotingId === member.uid ? "..." : "👑 Promote"}
                    </button>
                  )}
                  <button onClick={() => handleRemoveMember(member)} style={styles.removeBtn}>Remove</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16, border: "2px solid #dbeafe", background: "#f8fbff" }}>
        <h3 style={{ ...styles.sectionTitle, color: "#3b82f6" }}>Invite a Member</h3>
        <div style={{ display: "flex", gap: 16, marginBottom: 16, marginTop: 8 }}>
          {[{ num: "1", text: "Enter their email below" }, { num: "2", text: "They get a pop-up in Homie when they log in" }, { num: "3", text: "They click Accept — done!" }].map(step => (
            <div key={step.num} style={styles.howItWorksStep}>
              <div style={styles.howItWorksNum}>{step.num}</div>
              <div style={styles.howItWorksText}>{step.text}</div>
            </div>
          ))}
        </div>
        <form onSubmit={handleInvite} style={{ display: "flex", gap: 10 }}>
          <input className="field-input" type="email" placeholder="familymember@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ flex: 1 }} required />
          <button className="btn-ai" type="submit" disabled={loading} style={{ padding: "10px 20px", whiteSpace: "nowrap" }}>
            {loading ? "Sending..." : "Send Invite"}
          </button>
        </form>
        {inviteLink && (
          <div style={styles.inviteLinkBox}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#166534", marginBottom: 6 }}>Invite sent!</div>
            <div style={{ fontSize: 12, color: "#6b88b8", fontWeight: 600, marginBottom: 10 }}>Or share this backup link:</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={styles.linkText}>{inviteLink}</div>
              <button className="btn-ai" style={{ padding: "8px 14px", fontSize: 13, flexShrink: 0 }} onClick={handleCopyLink}>{copied ? "Copied!" : "Copy"}</button>
            </div>
          </div>
        )}
        {pendingInvites.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#6b88b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Pending Invites</div>
            {pendingInvites.map(inv => (
              <div key={inv.id} style={styles.pendingRow}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{inv.inviteeEmail}</span>
                <button onClick={() => handleCancelInvite(inv.id)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Store Settings */}
      <div className="card" style={{ marginBottom: 16 }}>
        <StoreSettings />
      </div>

      {!isOwner && (
        <div className="card" style={{ border: "2px solid #fecaca", background: "#fff5f5" }}>
          <h3 style={{ ...styles.sectionTitle, color: "#dc2626" }}>Leave Household</h3>
          <p style={{ fontSize: 13, color: "#7a3a3a", fontWeight: 600, marginBottom: 14, marginTop: 4 }}>You will lose access to all shared tasks.</p>
          <button className="btn-danger" onClick={handleLeave}>Leave Household</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  sectionTitle: { fontFamily: "'Fredoka One', cursive", fontSize: 18, color: "#8B4513" },
  memberRow: { display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #f5ead8", flexWrap: "wrap" },
  avatar: { flexShrink: 0 },
  avatarFallback: { width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg, #ff8c00, #f97316)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16 },
  youBadge: { background: "#f0fdf4", color: "#22c55e", borderRadius: 20, fontSize: 10, fontWeight: 800, padding: "2px 8px", marginLeft: 8, textTransform: "uppercase" },
  roleBadge: { padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: "uppercase", flexShrink: 0 },
  ownerBadge: { background: "#fef9c3", color: "#854d0e" },
  memberBadge: { background: "#f0f4ff", color: "#6366f1" },
  promoteBtn: { background: "#fef9c3", border: "2px solid #f59e0b", color: "#854d0e", padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, transition: "all 0.18s", whiteSpace: "nowrap" },
  removeBtn: { background: "#fff0f0", border: "2px solid #fecaca", color: "#dc2626", padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, transition: "all 0.18s" },
  inviteLinkBox: { marginTop: 14, padding: "14px 16px", background: "#eff6ff", borderRadius: 12, border: "1px solid #bfdbfe" },
  linkText: { flex: 1, fontSize: 12, color: "#1e40af", fontWeight: 600, background: "white", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 10px", wordBreak: "break-all" },
  pendingRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "white", borderRadius: 8, border: "1px solid #dbeafe", marginBottom: 6 },
  howItWorksStep: { flex: 1, background: "white", borderRadius: 12, padding: "12px", border: "1px solid #dbeafe", textAlign: "center" },
  howItWorksNum: { width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #6366f1)", color: "white", fontFamily: "'Fredoka One', cursive", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px" },
  howItWorksText: { fontSize: 12, fontWeight: 700, color: "#334155", lineHeight: 1.4 },
  successBanner: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", borderRadius: 12, padding: "12px 16px", fontSize: 14, fontWeight: 700, marginBottom: 16, cursor: "pointer" },
  errorBanner: { background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 12, padding: "12px 16px", fontSize: 14, fontWeight: 700, marginBottom: 16, cursor: "pointer" },
};
